<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RefreshCw } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { useProcurementApi } from '../composables/useProcurementApi'
import type { AgentOption } from '../types/procurement'

const { t } = useI18n()
const api = useProcurementApi()

const suppliers = ref<AgentOption[]>([])
const loading = ref(true)
const error = ref('')
const search = ref('')

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return suppliers.value

  return suppliers.value.filter((supplier) => supplier.name.toLowerCase().includes(term))
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    suppliers.value = await api.listSuppliers()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-heading">
    <div>
      <h2>{{ t('procurement.suppliers.title') }}</h2>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">
      <RefreshCw :size="15" aria-hidden="true" />
      {{ t('procurement.common.retry') }}
    </button>
  </div>

  <template v-else>
    <div class="toolbar">
      <input v-model="search" class="search-input" type="search" :placeholder="t('procurement.suppliers.searchPlaceholder')">
      <div class="summary">
        <span>{{ t('procurement.suppliers.count', { count: filtered.length }) }}</span>
      </div>
    </div>

    <p class="form-note">{{ t('procurement.suppliers.hint') }}</p>

    <div v-if="filtered.length === 0" class="panel">
      <span class="empty-inline">{{ t('procurement.suppliers.empty') }}</span>
    </div>

    <table v-else class="data-table">
      <thead>
        <tr>
          <th>{{ t('procurement.suppliers.name') }}</th>
          <th>{{ t('procurement.suppliers.code') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="supplier in filtered" :key="supplier.id">
          <td class="cell-name">{{ supplier.name }}</td>
          <td class="cell-code">#{{ supplier.id }}</td>
        </tr>
      </tbody>
    </table>
  </template>
</template>
